import React, { useState, useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

type AutoCompleteProps<T = any> = {
  /* eslint-disable */
  fetchFn: (query: string) => Promise<T[]>; // API call
  renderItem: (item: T) => React.ReactNode; // how to display suggestion
  getItemValue: (item: T) => string; // value for input box
  onSelect: (item: T) => void; // callback on selection
  /* eslint-enable */
  value?: T;
  placeholder?: string;
  minLength?: number;
  debounce?: number;
  label?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
};

export function AutoComplete<T>({
  fetchFn,
  renderItem,
  getItemValue,
  onSelect,
  value,
  placeholder = 'Search...',
  minLength = 2,
  debounce = 300,
  style = {},
  label,
  disabled,
}: AutoCompleteProps<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (query.length < minLength) {
      setResults([]);
      setLoading(false); // Ensure loading is false when query is too short
      return;
    }

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await fetchFn(query);
        setResults(data || []);
      } catch (err) {
        console.error('Autocomplete fetch error:', err);
        setResults([]);
      } finally {
        setLoading(false); // Ensure loading is reset after fetch
      }
    }, debounce);

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [query]); // Only re-run when query changes

  const handleSelect = (item: T) => {
    setQuery(getItemValue(item));
    setResults([]);
    onSelect(item);
  };

  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  function handleQueryChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setQuery(e.target.value);
  }

  return (
    <Box style={{ position: 'relative', ...style }}>
      <TextField
        disabled={disabled}
        value={query}
        onChange={handleQueryChange}
        placeholder={placeholder}
        label={label}
        fullWidth
        variant="outlined"
      />
      {loading && (
        <Box style={{ marginTop: '8px', textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {results.length > 0 && (
        <Paper
          style={{
            position: 'absolute',
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 2000,
            marginTop: '4px',
          }}
        >
          <List>
            {results.map((item, i) => (
              <ListItem key={i} button onClick={() => handleSelect(item)}>
                {renderItem(item)}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
